import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, XCircle, TrendingUp, Clock, Shield, Scissors, Sparkles, UserCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import InteractiveMap from '../components/InteractiveMap';

const Home = () => {
  // Vibe-coded animations
  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const springUp = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    show: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 }
    }
  };

  return (
    <>
      <Helmet>
        <title>RandevUPP 2.0 | İşletmenizi Kendi Markanızla Yönetin</title>
        <meta name="description" content="Kuaförler, güzellik salonları ve nail art stüdyoları için yeni nesil işletme yönetim ve randevu sistemi. RandevUPP 2.0 ile kendi markanızı büyütün." />
        <meta property="og:title" content="RandevUPP 2.0 | İşletmenizi Kendi Markanızla Yönetin" />
        <meta property="og:description" content="Kuaförler, güzellik salonları ve nail art stüdyoları için yeni nesil işletme yönetim ve randevu sistemi." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="flex flex-col items-center justify-center pt-16 pb-12 md:pt-24 md:pb-20 text-center"
      >


        <motion.h1 variants={springUp} className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 mb-6 max-w-4xl leading-tight">
          Kendi Markanızın Altında <br className="hidden md:block"/>
          İşletmenizi <span className="text-primary-500">Yönetin</span>
        </motion.h1>

        <motion.p variants={springUp} className="mt-6 text-base md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 px-4">
          Yeni nesil, modern ve kompakt arayüz devrimiyle tanışın. Müşterilerinize kendi alan adınızla hizmet verin, tüm verileriniz tamamen size kalsın.
        </motion.p>

        <motion.div variants={springUp} className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full px-4 sm:px-0 sm:w-auto">
          <Link to="/pricing" className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center hover:-translate-y-1 hover:shadow-primary-500/40">
            Sisteme Sahip Olun <ArrowRight size={18} />
          </Link>
          <Link to="/features" className="btn-secondary w-full sm:w-auto text-center hover:-translate-y-1">
            Tüm Özellikleri Gör
          </Link>
        </motion.div>
      </motion.div>

      {/* Target Audience Section */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-16 bg-gray-50/50 dark:bg-gray-900/20 rounded-[3rem] mb-16"
      >
        <motion.div variants={springUp} className="text-center mb-12 px-4">
          <h2 className="text-2xl md:text-4xl font-bold mb-4">Kimler İçin Tasarlandı?</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Hizmet sektörünün dinamiklerine uygun olarak özel geliştirilmiş altyapımızla işinizi kolaylaştırıyoruz.
          </p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-5xl mx-auto px-4">
          {[
            { icon: <Scissors size={32} />, title: "Kuaförler" },
            { icon: <Sparkles size={32} />, title: "Güzellik Salonları" },
            { icon: <UserCheck size={32} />, title: "Nail Art Stüdyoları" },
            { icon: <Shield size={32} />, title: "Dövme Stüdyoları" }
          ].map((item, idx) => (
            <motion.div key={idx} variants={springUp} className="glass-panel p-6 rounded-[2rem] flex flex-col items-center justify-center text-center gap-3 hover:-translate-y-2 transition-transform duration-300 group">
              <div className="text-primary-500 group-hover:scale-125 transition-transform duration-500">{item.icon}</div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">{item.title}</h3>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Interactive Map Section */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-16 md:py-24 mb-16 border-t border-b border-gray-100 dark:border-gray-800/50 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-50/30 dark:via-primary-900/5 to-transparent -z-10"></div>
        <motion.div variants={springUp} className="text-center mb-4 px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Büyüyen Ailemize Katılın</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg">
            Türkiye'nin dört bir yanındaki işletmeler RandevUPP ayrıcalığını yaşıyor. Henüz hizmet vermediğimiz bir şehirdeyseniz, <strong className="text-amber-500 dark:text-amber-400">ilk müşterimize özel %20 indirim</strong> fırsatını kaçırmayın!
          </p>
        </motion.div>
        
        <motion.div variants={springUp}>
          <InteractiveMap />
        </motion.div>
      </motion.section>

      {/* Value Proposition */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-12 md:py-16 px-4"
      >
        <motion.div variants={springUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">İşletmenize Neler Katar?</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Sadece bir randevu defteri değil, işletmenizi büyütecek akıllı bir ekosistem sunuyoruz.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { color: "primary", icon: <TrendingUp size={24} />, title: "Gelirlerinizi Artırın", desc: "Müşterilerinize doğrudan SMS veya Push bildirimleri atarak hatırlatmalar yapın, kampanya bildirimleriyle boş saatlerinizi doldurun." },
            { color: "blue", icon: <Clock size={24} />, title: "Zaman Kazanın", desc: "Telefon trafiğinden kurtulun. Müşterileriniz kendi hesaplarından randevularını oluştursun, personeliniz otomatik prim hesaplamalarıyla uğraşmasın." },
            { color: "emerald", icon: <Shield size={24} />, title: "Prestij ve Güven", desc: "İşletmenizi sıradan bir profil yerine kendi profesyonel alan adınızla (www.isletmeniz.com) temsil edin. Müşteri verileriniz güvende kalsın." }
          ].map((item, idx) => (
            <motion.div key={idx} variants={springUp} className="glass-panel p-8 rounded-[2.5rem] hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className={`w-14 h-14 bg-${item.color}-100 dark:bg-${item.color}-900/30 text-${item.color}-600 dark:text-${item.color}-400 rounded-2xl flex items-center justify-center mb-6 shadow-inner`}>
                {item.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* How It Works */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-20 bg-white/30 dark:bg-black/20 rounded-[3rem] mb-16 px-4"
      >
        <motion.div variants={springUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Sistem Nasıl Kurulur?</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            Hiçbir teknik bilgiye ihtiyacınız yok. Her şeyi biz hallediyoruz.
          </p>
        </motion.div>
        <div className="max-w-5xl mx-auto relative">
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-800 to-transparent -translate-y-1/2 z-0"></div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative z-10">
            {[
              { step: "1", title: "Bize Ulaşın", desc: "Paketinizi seçin ve işletme detaylarınızı bizimle paylaşın." },
              { step: "2", title: "Kurulumu Yapalım", desc: "Alan adınızı bağlayalım ve sisteminizi 24 saat içinde hazır hale getirelim." },
              { step: "3", title: "Yönetmeye Başlayın", desc: "Size özel panelinize giriş yapın, randevularınızı ve gelirinizi takip edin." }
            ].map((item, idx) => (
              <motion.div key={idx} variants={springUp} className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-white/50 dark:border-gray-700/50 text-center relative hover:-translate-y-2 transition-transform duration-300 group">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white font-black flex items-center justify-center text-2xl absolute -top-8 left-1/2 -translate-x-1/2 shadow-lg shadow-primary-500/40 group-hover:scale-110 transition-transform duration-300">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mt-6 mb-3 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Comparison Section */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-12 md:py-16 px-4"
      >
        <motion.div variants={springUp} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Neden RandevUPP 2.0?</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Klasik randevu sistemlerinin dayattığı kısıtlamalardan kurtulun. Gerçek özgürlük ve marka bilinirliği için tasarlandı.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Competitors */}
          <motion.div variants={springUp} className="glass-panel p-8 md:p-10 rounded-[3rem] border-red-100 dark:border-red-900/30 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-5 text-red-500 group-hover:scale-150 group-hover:rotate-12 transition-all duration-700">
              <XCircle size={150} />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8">Diğer Markalar</h3>
            <ul className="space-y-5">
              {[
                'Kendi Ekosistemleri (Sizi onların markasına bağlar)',
                'Verilerinizin güvenliği meçhul',
                'Yüksek ve sürekli artan fiyatlar',
                'Kısıtlı erişim ve özellik sınırları',
                'Kota limiti (Randevu/Personel)',
                'SMS gönderimleri için ek ücretler'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-4 text-gray-600 dark:text-gray-400 text-sm md:text-base font-medium">
                  <XCircle size={22} className="text-red-500 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* RandevUPP */}
          <motion.div variants={springUp} className="p-8 md:p-10 rounded-[3rem] bg-gradient-to-br from-primary-600 to-primary-900 text-white shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-10 text-white group-hover:scale-150 group-hover:rotate-12 transition-all duration-700">
              <CheckCircle2 size={150} />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-8">RandevUPP 2.0</h3>
            <ul className="space-y-5 relative z-10">
              {[
                'Kendi Markanız, Kendi Alan Adınız (isletmeniz.com)',
                'Kendi Veri Tabanınız (%100 Size Ait)',
                '%99.9 Uptime ile kesintisiz erişim',
                'Kesintisiz ve Hızlı Yazılım/Bakım Desteği',
                'Sürpriz Fiyat Artışı Yok',
                'Kota veya Limit Yok (Sınırsız Randevu/Personel)',
                'Ücretsiz Anlık Bildirimler (Push)'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-4 text-primary-50 text-sm md:text-base font-medium">
                  <CheckCircle2 size={22} className="text-primary-300 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="py-16 md:py-24 text-center px-4"
      >
        <motion.div variants={springUp} className="max-w-4xl mx-auto bg-gradient-to-tr from-gray-900 to-gray-800 dark:from-white dark:to-gray-100 p-10 md:p-16 rounded-[3rem] shadow-2xl overflow-hidden relative group">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white dark:text-gray-900 relative z-10">İşletmenizi Dijitale Taşıma Vakti!</h2>
          <p className="text-gray-300 dark:text-gray-600 mb-10 text-lg md:text-xl relative z-10 max-w-2xl mx-auto">
            RandevUPP 2.0 ile markanıza değer katın, müşterilerinize eşsiz bir deneyim yaşatın. Kurulum sürecini başlatmak için hemen iletişime geçin.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-3 text-lg font-bold px-10 py-5 bg-primary-500 text-white hover:bg-primary-400 rounded-full transition-all shadow-lg shadow-primary-500/50 hover:shadow-primary-500/80 hover:-translate-y-1 relative z-10">
            Hemen İletişime Geçin <ArrowRight size={24} />
          </Link>
        </motion.div>
      </motion.section>
    </>
  );
};

export default Home;
